import { useState } from "react";
import { useNavigate } from "react-router";
import { useDotLegalPostGetQueryArray, useDotLegalQuery } from "@common/hooks/dotLegalReactQuery";
import { useStateSessionStorage } from "@common/hooks/useStateSessionStorage";
import { EntityType } from "@common/Common.types";
import { FilterOperatorUi, ListFilter, ListFilterOperator } from "@common/api/filterTypes";
import { FilterOption, FilterOptionType } from "@common/components/filterPopover/FilterPopover.types";
import { GetBooleanData, GetListData } from "@common/components/filterPopover/subMenues/SubMenuDataHelper";
import { toPercentage } from "@common/utilities";
import { useTranslation } from "@localization/useTranslation";
import { useUrlProvider } from "../../useUrlProvider";
import { useResponsibleQuery } from "../../user/hooks/useResponsibleQuery";
import { getSelectableBusinessAreasUrl, useSelectableTagsQuery } from "@common/hooks/useSelectableItemQueries";
import { SelectableColoredItem } from "../../processingActivity/ProcessingActivity.types";
import { IdNamePair } from "../../customLists/customListView/CustomListView.types";
import { getRiskLevelIndicatorChipStyle } from "../../riskSettings/riskArea/riskAreaDialog/riskLevels/riskLevelIndicators";
import { useRiskAreaAssessmentLevel } from "../riskArea/RiskArea.hooks";
import { RiskAreaTab } from "../riskArea/RiskArea.types";
import { RiskGovernanceMode } from "../riskArea/RiskGovernanceModeSwitcher";
import {
    fromBoolFilter,
    organisationTypeFilterId,
    riskLevelFilterOptions,
    subjectTypeFilterOptions,
    toBoolFilter,
    toSubjectTypeFilter,
    toSubjectTypeRequest,
    yesNoFilterOptions,
} from "../riskFilterUtils";
import { entityTypeLabel, RiskOperationalSection } from "../riskOperationalOverview/RiskOperationalOverview.types";
import { isLevelOverAppetite } from "../riskAssessmentsOverview/RiskAssessmentsOverview.utils";
import {
    buildAssessedTotalLabel,
    buildCellCounts,
    buildCellMovements,
    getControlCoverage,
    PlottedSubject,
    resolveOperationalSection,
} from "./RiskGovernanceOverview.utils";
import {
    RiskDistributionHistoryResult,
    RiskEntityRow,
    RiskGovernanceEntity,
    RiskGovernanceOrganisationScenario,
    RiskGovernanceFilter,
    RiskGovernanceOverviewRequest,
    RiskGovernanceOverviewResult,
    RiskSnapshotDate,
    SelectedCell,
} from "./RiskGovernanceOverview.types";
import { RiskExposureMode } from "../riskExposure/RiskExposure.types";
import { useRiskExposureConfig } from "../riskExposure/useRiskExposureConfig";

const overviewUrl = (riskAreaId: string | undefined) => `/risk-areas/governance/${riskAreaId}/overview`;
const distributionHistoryUrl = (riskAreaId: string | undefined) => `/risk-areas/governance/${riskAreaId}/distribution-history`;
const entitySearchUrl = "/risk-areas/operational/entities";

const typeFilterId = "types";
const entityFilterId = "risk-entity-ids";
const responsibleFilterId = "responsibleIds";
const businessAreaFilterId = "businessAreaIds";
const tagFilterId = "tagIds";
const riskFilterId = "riskLevels";
const hasMitigationFilterId = "hasMitigation";
const overAppetiteFilterId = "overAppetite";
const riskAcceptanceFilterId = "hasRiskAcceptance";
const avoidanceFilterId = "hasAvoidance";
const showOnlyMineFilterId = "show-only-mine";

// Session-storage keys for the per-area governance view state. Shared with the PDF download so a printed
// overview reproduces exactly what the user is looking at (filters, mode and snapshot date).
export const riskGovernanceFilterKey = (areaKey: string) => `risk-governance-filter:${areaKey}`;
export const riskGovernanceSearchKey = (areaKey: string) => `risk-governance-search:${areaKey}`;
export const riskGovernanceModeKey = (areaKey: string) => `risk-governance-mode:${areaKey}`;
export const riskGovernanceAsOfKey = (areaKey: string) => `risk-governance-asof:${areaKey}`;

// The selected matrix cell is on-screen drill-down state only, so the PDF has no use for it.
const riskGovernanceCellKey = (areaKey: string) => `risk-governance-cell:${areaKey}`;

function toListFilter<T>(values: Array<T>, operator: FilterOperatorUi | undefined): ListFilter<T> | undefined {
    if (values.length === 0) return undefined;
    return { value: values, filterOperator: operator === FilterOperatorUi.Negative ? ListFilterOperator.NotIn : ListFilterOperator.In };
}

function extractIds(filter: ListFilter<unknown> | undefined): Array<string> {
    return (filter?.value ?? []).map((value) => String(value));
}

function extractOperator(filter: ListFilter<unknown> | undefined): FilterOperatorUi {
    return filter?.filterOperator === ListFilterOperator.NotIn ? FilterOperatorUi.Negative : FilterOperatorUi.Positive;
}

function toIdNamePairs(items: Array<{ id: string; name: string }> | undefined): Array<IdNamePair> {
    return (items ?? []).map((x) => ({ id: x.id, name: x.name }));
}

export function buildOverviewRequest(areaKey: string, filter: RiskGovernanceFilter, searchText: string): RiskGovernanceOverviewRequest {
    const subjectTypes = toSubjectTypeRequest(filter.types, filter.includeOrganisation);

    return {
        riskAreaId: areaKey,
        showOnlyMine: filter.showOnlyMine,
        types: subjectTypes.types,
        includeOrganisationSubject: subjectTypes.includeOrganisationSubject,
        entityIds: filter.entityIds,
        responsibleIds: filter.responsibleIds,
        businessAreaIds: filter.businessAreaIds,
        tagIds: filter.tagIds,
        riskLevels: filter.riskLevels,
        hasMitigation: filter.hasMitigation,
        overAppetite: filter.overAppetite,
        hasRiskAcceptance: filter.hasRiskAcceptance,
        hasAvoidance: filter.hasAvoidance,
        searchText: searchText.trim() || undefined,
    };
}

// After-mitigation is suspended while a point-in-time snapshot is active. Shared with the PDF download so the
// printed overview applies the same rule to the mode it reads back from session storage.
export const deriveAfterMitigation = (mode: RiskGovernanceMode, asOf: string | null) => mode === RiskGovernanceMode.AfterMitigation && asOf === null;

// The display half of the governance overview: fetches the overview for a given request/mode/point-in-time and
// derives every value the stat tiles, matrix and highest-risk table render. Shared by the on-screen overview
// (which layers filter UI, navigation and the snapshot dialog on top) and the PDF, which needs the same picture.
export function useRiskGovernanceData(riskAreaId: string | undefined, request: RiskGovernanceOverviewRequest, afterMitigation: boolean, asOf: string | null) {
    const { translateString, translateNumber } = useTranslation();
    const responsibleQuery = useResponsibleQuery();
    const { exposureEnabled } = useRiskExposureConfig();
    const { assessesEntities, assessesOrganisation } = useRiskAreaAssessmentLevel(riskAreaId);

    // Point-in-time cutoff = end of the chosen day in UTC, so the chosen date is inclusive.
    const asOfCutoff = asOf ? `${asOf}T23:59:59.999Z` : undefined;
    const overviewParams = new URLSearchParams({ afterMitigation: String(afterMitigation) });
    if (asOfCutoff) overviewParams.set("asOf", asOfCutoff);
    const url = `${overviewUrl(riskAreaId)}?${overviewParams.toString()}`;
    const { data, isLoading } = useDotLegalPostGetQueryArray<RiskGovernanceOverviewResult>([url, request], url, request, { enabled: !!riskAreaId });

    // Deliberately no afterMitigation in url or key: the chart always shows the actual (pre-mitigation)
    // history, so the mode toggle neither refetches nor changes it.
    const historyUrl = `${distributionHistoryUrl(riskAreaId)}${asOfCutoff ? `?asOf=${asOfCutoff}` : ""}`;
    const { data: distributionHistory, isLoading: distributionHistoryLoading } = useDotLegalPostGetQueryArray<RiskDistributionHistoryResult>(
        [historyUrl, request],
        historyUrl,
        request,
        { enabled: !!riskAreaId }
    );

    const scale = data?.scale ?? 0;
    const appetite = data?.appetite ?? 0;
    const entities = data?.assessedEntities ?? [];
    const organisationScenarios = data?.assessedOrganisationScenarios ?? [];
    const riskLevels = data?.riskLevels ?? [];

    const responsibleNamesById = new Map((responsibleQuery.userData ?? []).map((user) => [user.id, user.name]));
    const getResponsibleName = (responsibleId: string | null) => (responsibleId ? (responsibleNamesById.get(responsibleId) ?? "") : "");

    const toRow = (entity: RiskGovernanceEntity): RiskEntityRow => ({
        subject: { kind: "entity", entityType: entity.type, entityId: entity.entityId },
        id: entity.entityId,
        name: entity.name,
        typeLabel: entityTypeLabel(entity.type, translateString),
        responsibleName: getResponsibleName(entity.responsibleId),
        riskLevel: entity.riskLevel,
        riskLabel: entity.riskLabel,
        scale,
        canNavigate: entity.isInRiskArea,
    });

    // The organisation's rows are named by their scenario, and they never navigate: the organisation's assessments
    // live in a section of the operational tab, not at a page the way an entity's do.
    const toOrganisationRow = (scenario: RiskGovernanceOrganisationScenario): RiskEntityRow => ({
        subject: { kind: "organisation" },
        id: scenario.riskAssessmentId,
        name: scenario.name,
        typeLabel: translateString("organization"),
        responsibleName: getResponsibleName(scenario.responsibleId),
        riskLevel: scenario.riskLevel,
        riskLabel: scenario.riskLabel,
        scale,
        canNavigate: false,
    });

    // The Assessed tile mirrors the matrix population exactly: every subject actually plotted, entities at their
    // worst cell and organisation scenarios one row each. Scored-then-avoided subjects drop off the matrix, so
    // they are not counted here either.
    const assessedCount = entities.length + organisationScenarios.length;
    // Its denominator is every subject in the area's process: entities curated onto it, plus the scenarios put on
    // it at organisation level whether or not they are scored yet.
    const totalEntityCount = data?.totalEntities ?? 0;
    const totalOrganisationScenarioCount = data?.totalOrganisationScenarios ?? 0;
    const totalSubjects = totalEntityCount + totalOrganisationScenarioCount;
    // The tile's gap: subjects in the process that the headline leaves out, because they were never scored or
    // were scored and then avoided (both drop off the matrix the headline counts).
    const notAssessedCount = totalSubjects - assessedCount;
    const assessedProgress = toPercentage(assessedCount, totalSubjects);

    // Over appetite counts both kinds of subject too. Derived here rather than in SQL because both populations are
    // already in hand, which also keeps it equal to the matrix cells above the appetite by construction.
    const isOverAppetite = (subject: { riskLevel: number }) => subject.riskLevel > appetite;
    const overAppetiteCount = entities.filter(isOverAppetite).length + organisationScenarios.filter(isOverAppetite).length;

    // A cell's number counts both kinds of subject: the entities plotted there and the organisation scenarios
    // plotted there. Both row types carry the same four cell columns, so they count as one list.
    const plottedSubjects: Array<PlottedSubject> = [...entities, ...organisationScenarios];

    const cellCounts = buildCellCounts(plottedSubjects, scale, (subject) => [subject.consequence, subject.probability]);
    const currentCellCounts = afterMitigation
        ? buildCellCounts(plottedSubjects, scale, (subject) => [subject.currentConsequence, subject.currentProbability])
        : undefined;
    const cellMovements = currentCellCounts ? buildCellMovements(cellCounts, currentCellCounts) : undefined;

    const riskLevelIndicators = data?.riskLevelIndicators ?? [];

    const getRiskChipStyle = (level: number) => getRiskLevelIndicatorChipStyle(level, riskLevelIndicators);

    // The exposure stat tile: sums in Financial mode, averages in Scale mode. Follows the mode switcher like
    // the other tiles: gross in Current, after-mitigation values in After-mitigation mode.
    // Null hides the tile (exposure not configured); as-of mode disables it in the component like the overdue tile.
    const exposureTile = (() => {
        const exposure = data?.exposure;
        if (!data || !exposure || exposure.withExposureCount === 0) return null;
        const financial = data.exposureMode === RiskExposureMode.Financial;
        const format = (value: number | null) =>
            financial
                ? `${translateNumber(value ?? 0)} ${data.exposureCurrencyCode}`
                : `${translateNumber(value ?? 0, { maximumFractionDigits: 2 })} / ${data.exposureScaleUpperBound}`;
        const gross = financial ? exposure.grossSum : exposure.grossAverage;
        const mitigated = financial ? exposure.afterMitigationSum : exposure.afterMitigationAverage;
        return { value: format(afterMitigation ? mitigated : gross) };
    })();

    // The tile's slot is driven by the configuration (Scale or Financial), which is known before the overview
    // data loads, so the tile count never changes between the loading skeletons and the loaded row: no fifth
    // tile slides in, and none flashes then vanishes. An area with no exposure-bearing assessments renders a
    // dash instead of a misleading 0 (see the tsx), rather than dropping the tile entirely.
    const showExposureTile = exposureEnabled;

    // The control-coverage tile, counting both kinds of subject so its denominator still equals the over-appetite
    // tile. Zero in point-in-time mode (the query skips coverage there), which resolves to a null percentage, so
    // the tile shows the same dash the exposure and overdue tiles do. Counted on current risk levels only, so the
    // component also disables the tile in after-mitigation mode.
    const controlCoverage = getControlCoverage(
        (data?.entitiesOverAppetite ?? 0) + (data?.organisationScenariosOverAppetite ?? 0),
        (data?.entitiesFullyCovered ?? 0) + (data?.organisationScenariosFullyCovered ?? 0)
    );

    // What the tiles are counting, resolved here so no tile has to call an organisation scenario an entity. Both
    // read the area's assessment level rather than the counts: an area whose subjects are not scored yet counts
    // zero of them, and a zero says nothing about which unit the area assesses.
    const assessedTotalLabel = buildAssessedTotalLabel(
        totalEntityCount,
        totalOrganisationScenarioCount,
        assessesEntities,
        assessesOrganisation,
        translateString
    );
    // Empty while the areas menu is still resolving, for the same reason the label above is: neither level is known
    // yet, so the tile would otherwise name entities and flip to risks once the area's level arrives.
    const controlCoverageLabel =
        assessesEntities || assessesOrganisation
            ? translateString(assessesOrganisation ? "riskGovernanceControlCoverageOfRisks" : "riskGovernanceControlCoverageOf", {
                  covered: translateNumber(controlCoverage.fullyCovered),
                  total: translateNumber(controlCoverage.total),
              })
            : "";

    return {
        // data, entities and toRow are the raw material the on-screen overview needs for its cell drill-down; both
        // callers destructure them out rather than passing them on, so they stay internal to the two hooks.
        data,
        entities,
        organisationScenarios,
        toRow,
        toOrganisationRow,
        responsibleUsers: responsibleQuery.userData,
        isLoading,
        hasData: !!data && scale > 0,
        scale,
        appetite,
        assessedCount,
        totalSubjects,
        assessedTotalLabel,
        controlCoverageLabel,
        notAssessedCount,
        assessedProgress,
        overAppetiteCount,
        overduePlans: data?.overduePlans ?? 0,
        plansWithoutLinkedControls: data?.plansWithoutLinkedControls ?? 0,
        untreatedOverAppetiteCount: (data?.entitiesUntreatedOverAppetite ?? 0) + (data?.organisationScenariosUntreatedOverAppetite ?? 0),
        // The one count that stays entity-only: it asks which subjects are safe themselves but linked to something
        // over appetite, and the organisation is not a node in the entity graph that question is asked of.
        linkedRiskOverAppetiteCount: data?.entitiesWithLinkedRiskOverAppetite ?? 0,
        controlCoverage,
        consequenceLevels: data?.consequenceLevels ?? [],
        probabilityLevels: data?.probabilityLevels ?? [],
        riskLevelIndicators,
        riskLevels,
        matrix: data?.matrix ?? [],
        cellCounts,
        cellMovements,
        distributionHistory,
        distributionHistoryLoading,
        exposureTile,
        showExposureTile,
        getRiskChipStyle,
    };
}

export function useRiskGovernanceOverview(riskAreaId: string | undefined) {
    const { translateString } = useTranslation();
    const navigate = useNavigate();
    const { getRiskAssessmentsOverviewUrl, getRiskAreaUrl, getRiskOperationalOverviewUrl, getRiskMitigationOverviewUrl } = useUrlProvider();
    const businessAreaQuery = useDotLegalQuery<Array<SelectableColoredItem>>(getSelectableBusinessAreasUrl());
    const tagQuery = useSelectableTagsQuery();

    const [snapshotDialogOpen, setSnapshotDialogOpen] = useState(false);

    const areaKey = riskAreaId ?? "";
    // Read again here rather than threaded out of useRiskGovernanceData: the tile deep links below need to know
    // whether the area has an organisation section to send anyone to.
    const { assessesOrganisation } = useRiskAreaAssessmentLevel(riskAreaId);
    const [filter, setFilter] = useStateSessionStorage<RiskGovernanceFilter>(riskGovernanceFilterKey(areaKey), {});
    const [searchText, setSearchText] = useStateSessionStorage<string>(riskGovernanceSearchKey(areaKey), "");
    const [selectedCell, setSelectedCell] = useStateSessionStorage<SelectedCell | null>(riskGovernanceCellKey(areaKey), null);
    const [mode, setMode] = useStateSessionStorage<RiskGovernanceMode>(riskGovernanceModeKey(areaKey), RiskGovernanceMode.Current);
    // The chosen point-in-time date (yyyy-mm-dd) or null for "today". When set, after-mitigation is suspended.
    const [asOf, setAsOf] = useStateSessionStorage<string | null>(riskGovernanceAsOfKey(areaKey), null);
    const isAsOf = asOf !== null;
    const afterMitigation = deriveAfterMitigation(mode, asOf);

    const request = buildOverviewRequest(areaKey, filter, searchText);
    const { data, entities, organisationScenarios, toRow, toOrganisationRow, responsibleUsers, ...overview } = useRiskGovernanceData(
        riskAreaId,
        request,
        afterMitigation,
        asOf
    );

    // Both kinds of subject, so the table's count is the number the cell badge shows. Entities first, since the
    // organisation's scenarios read as an addition to them rather than as peers of one entity.
    const isInSelectedCell = (subject: { consequence: number; probability: number }) =>
        !!selectedCell && subject.consequence === selectedCell.consequence && subject.probability === selectedCell.probability;

    const cellEntities = selectedCell
        ? [...entities.filter(isInSelectedCell).map(toRow), ...organisationScenarios.filter(isInSelectedCell).map(toOrganisationRow)]
        : [];

    const selectedCellLabel =
        selectedCell && data
            ? `${data.consequenceLevels[selectedCell.consequence - 1] ?? ""} / ${data.probabilityLevels[selectedCell.probability - 1] ?? ""}`
            : "";

    const snapshotDatesUrl = `/risk-areas/governance/${riskAreaId}/snapshot-dates`;
    const snapshotDatesQuery = useDotLegalQuery<Array<RiskSnapshotDate>>(snapshotDatesUrl, { enabled: !!riskAreaId && snapshotDialogOpen });

    // The session-storage setter persists whatever it is handed, so the selection must be passed as a value here,
    // never as a functional updater.
    const onCellClick = (consequence: number, probability: number) => {
        const isSameCell = selectedCell?.consequence === consequence && selectedCell?.probability === probability;
        setSelectedCell(isSameCell ? null : { consequence, probability });
    };

    const clearCellFilter = () => setSelectedCell(null);

    // Only an entity row has an assessment page; an organisation scenario row never navigates, so it never asks
    // for a url either.
    const getEntityUrl = (row: RiskEntityRow) =>
        riskAreaId && row.subject.kind === "entity" ? getRiskAssessmentsOverviewUrl(riskAreaId, row.subject.entityType, row.subject.entityId) : "";

    const navigateToEntity = (row: RiskEntityRow) => {
        const url = getEntityUrl(row);
        if (url) navigate(url);
    };

    // The organisation sits alongside the entity types: it is a subject the area can assess, and the matrix
    // plots it, so it has to be filterable the same way. Split back out of the selection in onFilterChange.
    const typeOptions = subjectTypeFilterOptions(translateString);
    const yesNoOptions = yesNoFilterOptions(translateString);

    const filterOptions: Array<FilterOption> = [
        {
            id: showOnlyMineFilterId,
            label: translateString("show"),
            type: FilterOptionType.Boolean,
            value: filter.showOnlyMine ?? false,
            fixedData: [
                { id: "0", name: translateString("all") },
                { id: "1", name: translateString("my") },
            ],
        },
        {
            id: typeFilterId,
            label: translateString("type"),
            type: FilterOptionType.SelectField,
            value: [...extractIds(filter.types), ...(filter.includeOrganisation ? [organisationTypeFilterId] : [])],
            fixedData: typeOptions,
            filterOperator: extractOperator(filter.types),
        },
        {
            id: entityFilterId,
            label: translateString("entity"),
            type: FilterOptionType.SelectField,
            value: extractIds(filter.entityIds),
            searchUrl: entitySearchUrl,
            filterOperator: extractOperator(filter.entityIds),
        },
        {
            id: responsibleFilterId,
            label: translateString("responsible"),
            type: FilterOptionType.SelectField,
            value: extractIds(filter.responsibleIds),
            fixedData: toIdNamePairs(responsibleUsers),
            filterOperator: extractOperator(filter.responsibleIds),
        },
        {
            id: businessAreaFilterId,
            label: translateString("businessArea"),
            type: FilterOptionType.SelectField,
            value: extractIds(filter.businessAreaIds),
            fixedData: toIdNamePairs(businessAreaQuery.data),
            filterOperator: extractOperator(filter.businessAreaIds),
        },
        {
            id: tagFilterId,
            label: translateString("tags"),
            type: FilterOptionType.SelectField,
            value: extractIds(filter.tagIds),
            fixedData: toIdNamePairs(tagQuery.data),
            filterOperator: extractOperator(filter.tagIds),
        },
        {
            id: riskFilterId,
            label: translateString("risk"),
            type: FilterOptionType.SelectField,
            value: extractIds(filter.riskLevels),
            fixedData: riskLevelFilterOptions(overview.riskLevels),
            filterOperator: extractOperator(filter.riskLevels),
        },
        {
            id: hasMitigationFilterId,
            label: translateString("mitigations"),
            type: FilterOptionType.SelectField,
            value: fromBoolFilter(filter.hasMitigation),
            fixedData: yesNoOptions,
        },
        {
            id: overAppetiteFilterId,
            label: translateString("riskGovernanceOverAppetite"),
            type: FilterOptionType.SelectField,
            value: fromBoolFilter(filter.overAppetite),
            fixedData: yesNoOptions,
        },
        {
            id: riskAcceptanceFilterId,
            label: translateString("acceptances"),
            type: FilterOptionType.SelectField,
            value: fromBoolFilter(filter.hasRiskAcceptance),
            fixedData: yesNoOptions,
        },
        {
            id: avoidanceFilterId,
            label: translateString("avoidances"),
            type: FilterOptionType.SelectField,
            value: fromBoolFilter(filter.hasAvoidance),
            fixedData: yesNoOptions,
        },
    ];

    const [pinnedFilterIds, setPinnedFilterIds] = useState<Array<string>>([]);
    const onPinnedFilterIdsChange = (ids: Array<string> | undefined) => setPinnedFilterIds(ids ?? []);

    const onFilterChange = (updatedOptions: Array<FilterOption>) => {
        const next: RiskGovernanceFilter = { ...filter };
        updatedOptions.forEach((option) => {
            if (option.id === showOnlyMineFilterId) {
                next.showOnlyMine = GetBooleanData(option.value) === "1";
                return;
            }

            const values = GetListData(option.value);
            switch (option.id) {
                case typeFilterId: {
                    // Not toListFilter: the organisation shares this selection, so the entity list can empty out
                    // while the filter still holds an operator that has to survive.
                    const subjectTypes = toSubjectTypeFilter(values, option.filterOperator);
                    next.types = subjectTypes.types;
                    next.includeOrganisation = subjectTypes.includeOrganisation;
                    break;
                }
                case entityFilterId:
                    next.entityIds = toListFilter(values, option.filterOperator);
                    break;
                case responsibleFilterId:
                    next.responsibleIds = toListFilter(values, option.filterOperator);
                    break;
                case businessAreaFilterId:
                    next.businessAreaIds = toListFilter(values, option.filterOperator);
                    break;
                case tagFilterId:
                    next.tagIds = toListFilter(values, option.filterOperator);
                    break;
                case riskFilterId:
                    next.riskLevels = toListFilter(values.map(Number), option.filterOperator);
                    break;
                case hasMitigationFilterId:
                    next.hasMitigation = toBoolFilter(values);
                    break;
                case overAppetiteFilterId:
                    next.overAppetite = toBoolFilter(values);
                    break;
                case riskAcceptanceFilterId:
                    next.hasRiskAcceptance = toBoolFilter(values);
                    break;
                case avoidanceFilterId:
                    next.hasAvoidance = toBoolFilter(values);
                    break;
            }
        });

        setSelectedCell(null);
        setFilter(next);
    };

    const onSearchTextChange = (value: string) => {
        setSelectedCell(null);
        setSearchText(value ?? "");
    };

    const hasActiveFilters = searchText.trim().length > 0 || Object.values(filter).some((value) => value !== undefined && value !== false);

    const clearFilter = () => {
        setSelectedCell(null);
        setFilter({});
        setSearchText("");
    };

    // The posture filters (risk level, mitigation, over-appetite, acceptance, avoidance) read live treatment/
    // revision pointers, which are not point-in-time; hide them while viewing a snapshot. Entity-attribute
    // filters (type, entity, responsible, business area, tags) remain valid back in time.
    const asOfHiddenFilterIds = [riskFilterId, hasMitigationFilterId, overAppetiteFilterId, riskAcceptanceFilterId, avoidanceFilterId];
    const visibleFilterOptions = isAsOf ? filterOptions.filter((option) => !asOfHiddenFilterIds.includes(option.id)) : filterOptions;

    const onOpenSnapshotDialog = () => setSnapshotDialogOpen(true);
    const onCloseSnapshotDialog = () => setSnapshotDialogOpen(false);
    const onSelectSnapshotDate = (date: string) => {
        setAsOf(date.slice(0, 10));
        setSnapshotDialogOpen(false);
        setSelectedCell(null);
    };
    const onReturnToToday = () => {
        setAsOf(null);
        setSelectedCell(null);
    };
    const historyLink = asOf ? `${getRiskAreaUrl(areaKey, RiskAreaTab.History)}&historyTo=${asOf}` : undefined;

    // Every deep link below names the section it should open on, and both of the tab's tables know how to seed the
    // filter params, so each figure equals the rows it lands on again. A tile counts entities and the organisation's
    // scenarios together while the tab shows one section at a time, so the link follows the half the figure came
    // from: its entity share decides (see resolveOperationalSection). Only the section that opens is seeded — the
    // table that mounts consumes the params and strips them — so on an area that assesses both and draws from both
    // halves, the link opens on the entity section and the organisation section keeps whatever filter it held.
    const overAppetiteEntityShare = entities.filter((entity) => isLevelOverAppetite(entity.riskLevel, data?.appetite ?? 0)).length;
    const untreatedOverAppetiteEntityShare = data?.entitiesUntreatedOverAppetite ?? 0;
    const notAssessedEntityShare = (data?.totalEntities ?? 0) - entities.length;
    const notFullyCoveredEntityShare = (data?.entitiesOverAppetite ?? 0) - (data?.entitiesFullyCovered ?? 0);
    const sectionFor = (entityShare: number) => resolveOperationalSection(entityShare, assessesOrganisation);

    // The over-appetite tile's deep link, filtered to the subjects it counts. Only in current live mode: the
    // operational filter reads current levels, so in after-mitigation mode (where the tile counts projected
    // levels) and in point-in-time mode the link would land on a different set.
    const overAppetiteUrl =
        !isAsOf && !afterMitigation ? getRiskOperationalOverviewUrl(areaKey, { overAppetite: true, section: sectionFor(overAppetiteEntityShare) }) : undefined;

    // The over-appetite tile's gap line: of those subjects, the ones whose over-appetite risk carries no treatment
    // decision. Unguarded, unlike the headline above: the component hides this line outright in after-mitigation and
    // point-in-time mode, so a guard here would only state the same rule in a second place it could drift from. The
    // guards that remain are the load-bearing ones, where the figure itself still renders in the alternative mode.
    const untreatedOverAppetiteUrl = getRiskOperationalOverviewUrl(areaKey, {
        overAppetiteWithoutTreatment: true,
        section: sectionFor(untreatedOverAppetiteEntityShare),
    });

    // The linked-risk tile's landing set: within appetite themselves (Nej) but linked to something over it (Ja).
    // Current live mode only, for the same reason as the over-appetite link. No section of its own: the question is
    // about an entity's one-hop graph neighbours, and the organisation is not a node in that graph, so this figure
    // is entity-only by construction.
    const linkedRiskOverAppetiteUrl =
        !isAsOf && !afterMitigation
            ? getRiskOperationalOverviewUrl(areaKey, { overAppetite: false, linkedRiskOverAppetite: true, section: RiskOperationalSection.Entities })
            : undefined;

    // The Assessed tile's gap link: the subjects on the area the tile leaves uncounted. Current live mode only,
    // for the same reason as the over-appetite link, and because the operational table only ever shows today.
    const notAssessedUrl =
        !isAsOf && !afterMitigation ? getRiskOperationalOverviewUrl(areaKey, { notAssessed: true, section: sectionFor(notAssessedEntityShare) }) : undefined;

    return {
        pinnedFilterIds,
        onPinnedFilterIdsChange,
        ...overview,
        overAppetiteUrl,
        untreatedOverAppetiteUrl,
        linkedRiskOverAppetiteUrl,
        notAssessedUrl,
        // Deep links from the gap figures to the table that lists them, with the matching filter applied (each
        // overview seeds its filter from the param once, then strips it). Both of the overdue tile's figures land on
        // the mitigation table, whose rows are plans at either level, so they need no section; the coverage red band
        // lands on the operational table and picks its section the way the links above do.
        // Neither carries a mode guard: plan deadlines and control links are the same today whichever way the
        // matrix is being read, and the tile itself is disabled in point-in-time mode.
        overduePlansUrl: getRiskMitigationOverviewUrl(areaKey, { overdue: true }),
        plansWithoutLinkedControlsUrl: getRiskMitigationOverviewUrl(areaKey, { withoutControls: true }),
        notFullyCoveredUrl: getRiskOperationalOverviewUrl(areaKey, {
            overAppetiteWithoutControls: true,
            section: sectionFor(notFullyCoveredEntityShare),
        }),
        cellEntities,
        selectedCell,
        selectedCellLabel,
        onCellClick,
        clearCellFilter,
        navigateToEntity,
        getEntityUrl,
        filterOptions: visibleFilterOptions,
        onFilterChange,
        searchText,
        onSearchTextChange,
        hasActiveFilters,
        clearFilter,
        mode,
        onModeChange: setMode,
        isAsOf,
        afterMitigation,
        asOf,
        snapshotDialogOpen,
        snapshotDates: snapshotDatesQuery.data ?? [],
        snapshotDatesLoading: snapshotDatesQuery.isLoading,
        onOpenSnapshotDialog,
        onCloseSnapshotDialog,
        onSelectSnapshotDate,
        onReturnToToday,
        historyLink,
    };
}
